// @ts-strict-ignore
import {
  ExpressionOperatorString,
  WorkflowImportUpdateResultType, WorkflowTransformationIssueType,
} from './constants';
import type {
  Action,
  ActionOption,
  Expression,
  List,
  ObjectIdReverseTransformationMap,
  ObjectIdTransformationMap,
  ServiceAttributesById,
  Services,
} from './types';
import { resolveServiceOrUniversalAttribute } from 'flow/service/thresholds';

export function remapExpressionDataRouteIds({
  expressionArray,
  dataRouteIdMap,
}: {
  expressionArray: Expression;
  dataRouteIdMap: ObjectIdTransformationMap | ObjectIdReverseTransformationMap;
}) {
  if (!expressionArray) {
    return null;
  }

  return expressionArray.map(
    expressionItem => {
      if (Array.isArray(expressionItem)) {
        return remapExpressionDataRouteIds({
          expressionArray: expressionItem as Expression,
          dataRouteIdMap,
        });
      }

      if (typeof expressionItem === 'object'
        && expressionItem !== null && expressionItem.data_route_id
        && dataRouteIdMap[expressionItem.data_route_id]) {
        return {
          data_route_id: dataRouteIdMap[expressionItem.data_route_id],
        };
      }

      return expressionItem;
    });
}

export function createRangeOfNumbers(start, stop, step) {
  return Array.from({ length: (stop - start) / (step + 1) }, (_, i) => start + (i * step));
}

export function createStringOfPathArray(array): string {
  return array.reduce((result, path) => {
    if (path.includes(']')) {
      return `${result}[${path}`;
    }

    if (!isNaN(Number(path))) {
      return `${result}[${path}]`;
    }

    return result.length > 0
      ? `${result}.${path}`
      : path;
  }, '');
}

export function remapListIdsForListServiceAction({
  actionOptionValue,
  action,
  lists,
}: {
  actionOptionValue: ActionOption;
  action: Action;
  lists: List[];
}) {
  const listIdOfTheAction = actionOptionValue.value;
  const matchingList = lists.find(list => list.customer_list_id === Number(listIdOfTheAction));

  if (matchingList) {
    return {
      value: matchingList.customer_list_id,
      type: null,
    };
  }

  const matchingListByServiceName = lists.find(list => list.list_name === action.service.name);
  if (matchingListByServiceName) {
    return {
      value: matchingListByServiceName.customer_list_id,
      type: WorkflowImportUpdateResultType.LIST_ID_UPDATED,
    };
  }

  return {
    value: null,
    type: WorkflowTransformationIssueType.NO_MATCHING_LIST_ID_IN_CUSTOMER,
  };
}

export function getAttributeToken({
  action,
  attributeId,
  services,
  universalAttributes,
}: {
  action: Action;
  attributeId: number;
  services: Services;
  universalAttributes: ServiceAttributesById;
}) {
  if (attributeId && action?.service) {
    const matchingServiceObject = Object.values(services).find(
      service => service.internal_name === action.service.internalName);
    const attribute = resolveServiceOrUniversalAttribute(
      matchingServiceObject?.attributes,
      universalAttributes,
      attributeId,
    );
    if (attribute) {
      // sometimes when a workflow is cloned to a "target" customer,
      // certain attributes are unique to the "source" customer
      return attribute.attribute_token;
    }
  }
  return null;
}

/**
 * check if provided string item is one of expression operator strings
 */
export function getIsExpressionOperatorString(item: string) {
  return ([
    ExpressionOperatorString.PLUS,
    ExpressionOperatorString.DIVIDE,
    ExpressionOperatorString.MINUS,
    ExpressionOperatorString.MULTIPLY,
  ].includes(item));
}
